import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import { useInvestigation } from '../../../contexts/InvestigationContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import InvestigatorSelector from '../components/InvestigatorSelector';
import ContradictionChecklist from '../components/ContradictionChecklist';
import CaseAuditTimeline from '../components/CaseAuditTimeline';
import EvidenceRequestModal from '../components/EvidenceRequestModal';
import InvestigatorHandoffModal from '../components/InvestigatorHandoffModal';
import EvidenceReviewWorkspace from '../components/EvidenceReviewWorkspace';
import RUHActionPlan from '../components/RUHActionPlan';
import { getDeadlineData, getStatusUI } from '../../../utils/deadlineRules';
import { exportSecureAuditPackage } from '../../../utils/exportUtils';
import supabase from '../../../supabase/supabase';

const {
  FiArrowLeft, FiClock, FiCheckCircle, FiAlertTriangle, FiShield, FiInfo, 
  FiExternalLink, FiBookOpen, FiX, FiCalendar, FiAlertCircle, FiFileText, 
  FiPlus, FiUserPlus, FiActivity, FiDownload, FiLoader, FiCornerUpLeft, FiFile,
  FiTarget
} = FiIcons;

const ComplianceClock = ({ report }) => {
  const deadline = getDeadlineData(report);
  const ui = getStatusUI(deadline.level);
  const isConfirmed = report.is_confirmed || report.isConfirmed;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl p-6 border shadow-sm space-y-6 ${deadline.level > 1 ? 'border-red-200 bg-red-50/10' : 'border-gray-100 dark:border-gray-700'}`}>
      <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2 uppercase text-xs tracking-widest">
        <SafeIcon icon={ui.icon} className={`text-${ui.color}-600`} />
        {ui.label}: Compliance
      </h3>
      <div className="space-y-4">
        {/* Bekreftelsesfrist */}
        <div className="space-y-2">
          <div className="flex justify-between text-[10px] font-black uppercase tracking-wider">
            <span className="text-gray-400">Mottaksbekreftelse (7 dager)</span>
            <span className={isConfirmed ? 'text-green-500' : (deadline.status.includes('ack') ? 'text-red-500' : 'text-blue-500')}>
              {isConfirmed ? 'Fullført' : (deadline.status.includes('ack') ? 'Frist utgått' : 'I rute')}
            </span>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
            <div className={`h-full transition-all duration-1000 ${isConfirmed ? 'bg-green-500' : (deadline.status.includes('ack') ? 'bg-red-500' : 'bg-blue-500')}`} style={{ width: isConfirmed ? '100%' : '50%' }} />
          </div>
        </div>
        {/* Svarfrist Omvarslet */}
        {deadline.deadlineReported && (
          <div className="space-y-2">
            <div className="flex justify-between text-[10px] font-black uppercase tracking-wider">
              <span className="text-gray-400">Svarfrist Omvarslet</span>
              <span className={deadline.hasReportedReplied ? 'text-green-500' : (deadline.status.includes('reported') ? 'text-red-500' : 'text-amber-500')}>
                {deadline.hasReportedReplied ? 'Fullført' : (deadline.status.includes('reported') ? 'Frist utgått' : 'Venter')}
              </span>
            </div>
            <div className="w-full bg-gray-100 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
              <div className={`h-full transition-all duration-1000 ${deadline.hasReportedReplied ? 'bg-green-500' : (deadline.status.includes('reported') ? 'bg-red-500' : 'bg-amber-500')}`} style={{ width: deadline.hasReportedReplied ? '100%' : '50%' }} />
            </div>
          </div>
        )}
        {/* Tilbakemeldingsfrist */}
        <div className="space-y-2">
          <div className="flex justify-between text-[10px] font-black uppercase tracking-wider">
            <span className="text-gray-400">Saksbehandling (3 måneder)</span>
            <span className={report.status === 'resolved' ? 'text-green-500' : (deadline.status.includes('feedback') ? 'text-red-500' : 'text-indigo-500')}>
              {report.status === 'resolved' ? 'Løst' : (deadline.status.includes('feedback') ? 'Frist utgått' : 'I rute')}
            </span>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
            <div className={`h-full transition-all duration-1000 ${report.status === 'resolved' ? 'bg-green-500' : (deadline.status.includes('feedback') ? 'bg-red-500' : 'bg-indigo-500')}`} style={{ width: report.status === 'resolved' ? '100%' : '50%' }} />
          </div>
        </div>
      </div>
      {deadline.level > 0 && (
        <p className={`text-[11px] font-bold text-${ui.color}-700 dark:text-${ui.color}-400 leading-tight`}>
          {deadline.message}
        </p>
      )}
    </div>
  );
};

const ReportDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { reports, updateReport, companies } = useData();
  const { investigations } = useInvestigation();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [showHandoffModal, setShowHandoffModal] = useState(false);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isExporting, setIsExporting] = useState(false);
  const [appeal, setAppeal] = useState(null);
  const [outcomeData, setOutcomeData] = useState({
    outcome_type: 'no_violation',
    outcome_summary: '',
    remediation_action: '',
    root_cause: ''
  });

  const report = reports.find(r => r.id === id);
  const company = companies.find(c => c.id === report?.company_id);
  const investigation = investigations?.find(i => i.reportId === id);

  useEffect(() => {
    if (id) {
      fetchAssignment();
      fetchAppeal();
    }
  }, [id]);

  useEffect(() => {
    if (report && report.outcome_type) {
      setOutcomeData({
        outcome_type: report.outcome_type,
        outcome_summary: report.outcome_summary || '',
        remediation_action: report.remediation_action || '',
        root_cause: report.root_cause || ''
      });
    }
  }, [report]);

  const fetchAssignment = async () => {
    try {
      const { data, error } = await supabase
        .from('utreder_assignments')
        .select(`*, utredere (full_name, company_name)`)
        .eq('report_id', id)
        .order('assigned_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setAssignment(data);
    } catch (err) {
      console.error('Error fetching assignment:', err);
    }
  };

  const fetchAppeal = async () => {
    try {
      const { data } = await supabase
        .from('case_appeals_1788622000000')
        .select('*')
        .eq('report_id', id)
        .maybeSingle();
      setAppeal(data);
    } catch (err) {
      console.error('Error fetching appeal:', err);
    }
  };

  const handleExportAuditPackage = async () => {
    setIsExporting(true);
    try {
      const { data: evidence } = await supabase
        .from('evidence_items_1788616000000')
        .select('*')
        .eq('report_id', id);
      
      const { data: logs } = await supabase
        .from('evidence_logs_1788616000000')
        .select('*')
        .order('created_at', { ascending: true });

      await exportSecureAuditPackage({
        report,
        evidence: evidence || [],
        logs: logs || [],
        assignment,
        organization: company
      });
    } catch (err) {
      console.error("Export failed:", err);
      alert("Kunne ikke generere revisjonspakke.");
    } finally {
      setIsExporting(false);
    }
  };

  if (!report) return <div className="p-8 animate-pulse text-gray-400">Laster saksdetaljer...</div>;

  const deadline = getDeadlineData(report);
  const deadlineUI = getStatusUI(deadline.level);
  const isRUH = report.type === 'ruh' || report.type === 'hms';

  const handleConfirmReceipt = async () => {
    try {
      const result = await updateReport(report.id, {
        is_confirmed: true,
        confirmed_at: new Date().toISOString(),
        history: [...(report.history || []), {
          date: new Date().toISOString(),
          action: 'Mottaksbekreftelse sendt til varsler',
          user: user.name
        }]
      });
      if (!result.success) throw new Error(result.error);
    } catch (err) {
      console.error("Feil ved bekreftelse:", err);
      alert("Kunne ikke bekrefte mottak. Vennligst prøv igjen.");
    }
  };

  const handleCloseCase = async () => {
    try {
      await updateReport(id, {
        status: 'resolved',
        closed_at: new Date().toISOString(),
        ...outcomeData,
        history: [...(report.history || []), {
          date: new Date().toISOString(),
          action: `Saken avsluttet med utfall: ${outcomeData.outcome_type}`,
          user: user.name
        }]
      });
      setShowOutcomeModal(false);
    } catch (err) {
      console.error('Error closing case:', err);
      alert("Kunne ikke avslutte saken.");
    }
  };

  const handleResolveAppeal = async (status, outcome) => {
    try {
      const { error } = await supabase
        .from('case_appeals_1788622000000')
        .update({ status, outcome })
        .eq('id', appeal.id);

      if (error) throw error;
      setAppeal(prev => ({ ...prev, status, outcome }));
      
      if (status === 'resolved') {
        await updateReport(id, {
          history: [...(report.history || []), {
            date: new Date().toISOString(),
            action: `Anke behandlet. Resultat: ${outcome}`,
            user: user.name
          }]
        });
      }
      alert("Anken er behandlet.");
    } catch (err) {
      console.error("Appeal resolution error:", err);
    }
  };

  const isConfirmed = report.is_confirmed || report.isConfirmed;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 font-sans">
      <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 sticky top-0 z-30 px-6 py-4 shadow-sm">
        <div className="max-w-7xl auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/company/dashboard')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl flex items-center gap-2 text-gray-600 dark:text-gray-300 transition-colors">
              <SafeIcon icon={FiArrowLeft} />
              <span className="text-xs font-black uppercase tracking-widest">Tilbake</span>
            </button>
            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 mx-2"></div>
            <h1 className="text-xl font-black dark:text-white truncate max-w-md">{report.title}</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={handleExportAuditPackage} disabled={isExporting} className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-200 transition-all flex items-center gap-2">
              {isExporting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiDownload} />}
              Revisjonspakke
            </button>
            {!isConfirmed && (
              <button onClick={handleConfirmReceipt} className="bg-green-600 text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-green-700 transition-all shadow-lg shadow-green-900/10 flex items-center gap-2">
                <SafeIcon icon={FiCheckCircle} />
                Bekreft mottak
              </button>
            )}
            {report.status !== 'resolved' && (
              <button onClick={() => setShowOutcomeModal(true)} className="bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-900/10 flex items-center gap-2">
                <SafeIcon icon={FiCheckCircle} />
                Avslutt sak
              </button>
            )}
            <button onClick={() => setShowEvidenceModal(true)} className="bg-gray-900 text-white px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-black transition-all">
              <SafeIcon icon={FiPlus} />
              Etterspør Bevis
            </button>
            {!assignment && (
              <button onClick={() => setShowHandoffModal(true)} className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-900/20 flex items-center gap-2 hover:bg-blue-700 transition-all">
                <SafeIcon icon={FiUserPlus} />
                Overfør til utreder
              </button>
            )}
            <button onClick={() => setShowAssignModal(true)} className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all">
              <SafeIcon icon={FiShield} />
              {investigation || report.assignedInvestigatorId ? 'Administrer Utredning' : 'Start Utredning'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {appeal && (
          <div className="mb-8 bg-amber-900 text-white rounded-3xl p-8 shadow-2xl relative overflow-hidden group border border-amber-800">
            <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:scale-125 transition-transform duration-1000"></div>
            <div className="flex items-start justify-between gap-8 relative z-10">
              <div className="flex gap-6">
                <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-md h-fit">
                  <SafeIcon icon={FiCornerUpLeft} className="text-3xl text-amber-200" />
                </div>
                <div>
                  <h3 className="text-xl font-black uppercase tracking-tight">Anke mottatt fra omvarslet</h3>
                  <p className="text-amber-200 text-[10px] font-black uppercase tracking-widest mt-1">Status: {appeal.status === 'pending' ? 'Venter på behandling' : appeal.status === 'reviewing' ? 'Under vurdering' : 'Ferdigbehandlet'}</p>
                  <div className="mt-6 bg-white/10 p-6 rounded-2xl border border-white/10">
                    <p className="text-sm font-medium leading-relaxed italic">"{appeal.reason}"</p>
                    {appeal.evidence_urls?.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {appeal.evidence_urls.map((url, idx) => (
                          <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-lg text-[10px] font-bold hover:bg-white/20 transition-colors">
                            <SafeIcon icon={FiFile} />
                            Vedlegg {idx + 1}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                {appeal.status !== 'resolved' ? (
                  <>
                    <button onClick={() => handleResolveAppeal('reviewing', '')} className="bg-white text-amber-900 px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-amber-50 transition-all">
                      Start vurdering
                    </button>
                    <button onClick={() => handleResolveAppeal('resolved', 'Anken er tatt til følge. Konklusjonen vil bli revidert.')} className="bg-green-500 text-white px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-green-600 transition-all">
                      Godkjenn Anke
                    </button>
                    <button onClick={() => handleResolveAppeal('resolved', 'Anken er avslått. Opprinnelig konklusjon opprettholdes.')} className="bg-red-500 text-white px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-red-600 transition-all">
                      Avslå Anke
                    </button>
                  </>
                ) : (
                  <div className="bg-white/20 px-6 py-4 rounded-2xl text-center">
                    <p className="text-[10px] font-black uppercase tracking-widest mb-1">Resultat</p>
                    <p className="text-xs font-bold">{appeal.outcome}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {deadline.level > 0 && (
          <div className={`mb-8 p-6 rounded-3xl border flex items-start gap-4 bg-${deadlineUI.color}-50 border-${deadlineUI.color}-100`}>
            <div className={`p-3 bg-${deadlineUI.color}-100 text-${deadlineUI.color}-600 rounded-xl`}>
              <SafeIcon icon={deadlineUI.icon} className="text-xl" />
            </div>
            <div>
              <h3 className={`font-black text-xs text-${deadlineUI.color}-900 uppercase tracking-widest`}>
                OPPFØLGING KREVES: {deadlineUI.label}
              </h3>
              <p className={`text-sm text-${deadlineUI.color}-700 mt-1 font-bold`}>
                {deadline.message}
              </p>
              <div className="mt-3 flex gap-4 text-[10px] font-black text-gray-400 uppercase tracking-wider">
                <span className="flex items-center gap-1.5">
                  <SafeIcon icon={FiCalendar} />
                  Mottaksfrist: {deadline.deadlineAck.toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1.5">
                  <SafeIcon icon={FiClock} />
                  Tilbakemeldingsfrist: {deadline.deadlineFeedback.toLocaleDateString()}
                </span>
                {deadline.deadlineReported && (
                  <span className="flex items-center gap-1.5 text-amber-600">
                    <SafeIcon icon={FiCalendar} />
                    Svarfrist Omvarslet: {deadline.deadlineReported.toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {assignment && (
          <div className="mb-8 bg-blue-500/10 border border-blue-500/20 rounded-3xl p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/20 flex items-center justify-center text-blue-500 shadow-lg">
                <SafeIcon icon={FiShield} size={24} />
              </div>
              <div>
                <h3 className="font-black text-blue-900 dark:text-blue-100 uppercase text-xs tracking-widest">Overført til ekstern utreder</h3>
                <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
                  Saken behandles av <strong>{assignment.utredere?.full_name}</strong> ({assignment.utredere?.company_name || 'Selvstendig'}).
                </p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="text-[10px] uppercase text-blue-400 font-black tracking-widest mb-1">Habilitet</div>
                <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase border tracking-widest ${assignment.conflict_check_status === 'pending' ? 'bg-amber-500/20 text-amber-600 border-amber-500/30' : assignment.conflict_check_status === 'cleared' ? 'bg-green-500/20 text-green-600 border-green-500/30' : 'bg-red-500/20 text-red-600 border-red-500/30'}`}>
                  {assignment.conflict_check_status === 'pending' ? 'Venter' : assignment.conflict_check_status === 'cleared' ? 'Habil' : 'Inhabil'}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase text-blue-400 font-black tracking-widest mb-1">Status</div>
                <div className="px-4 py-1.5 bg-blue-500/20 text-blue-600 dark:text-blue-300 rounded-full text-[10px] font-black uppercase border border-blue-500/30 tracking-widest">
                  {assignment.status === 'assigned' ? 'Venter på aksept' : 'Under utredning'}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-8 p-1.5 bg-gray-100 dark:bg-gray-800 rounded-2xl w-fit">
          <button onClick={() => setActiveTab('overview')} className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'overview' ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>
            <SafeIcon icon={FiFileText} className="inline mr-2" />
            Oversikt
          </button>
          <button onClick={() => setActiveTab('evidence')} className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'evidence' ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>
            <SafeIcon icon={FiActivity} className="inline mr-2" />
            Bevis-Arbeidsområde
          </button>
          {isRUH && (
            <button onClick={() => setActiveTab('actions')} className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'actions' ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>
              <SafeIcon icon={FiCheckCircle} className="inline mr-2" />
              Tiltaksplan
            </button>
          )}
          <button onClick={() => setActiveTab('audit')} className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'audit' ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>
            <SafeIcon icon={FiClock} className="inline mr-2" />
            Audit Trail
          </button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-8 space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-[32px] p-10 border border-gray-100 dark:border-gray-700 shadow-sm">
                  <div className="flex justify-between items-start mb-8 pb-6 border-b dark:border-gray-700">
                    <div>
                      <h2 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Beskrivelse av forholdet</h2>
                      <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed whitespace-pre-wrap font-medium">
                        {report.description}
                      </p>
                    </div>
                    {(report.externalHandling || assignment) && (
                      <span className="bg-purple-100 text-purple-700 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-purple-200">
                        Ekstern Håndtering
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Rapportør</p>
                      <p className="font-bold text-gray-900 dark:text-white">{report.isAnonymous ? 'Anonym' : report.reporterName}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Kategori</p>
                      <p className="font-bold text-gray-900 dark:text-white capitalize">{report.category}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Alvorlighetsgrad</p>
                      <span className={`font-black uppercase text-[10px] px-2 py-0.5 rounded ${report.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                        {report.severity}
                      </span>
                    </div>
                  </div>
                </div>

                {report.status === 'resolved' && (
                  <div className="bg-green-50 dark:bg-green-900/10 rounded-[32px] border border-green-100 dark:border-green-800/50 p-8 shadow-sm">
                    <h3 className="text-lg font-black text-green-900 dark:text-green-100 mb-6 flex items-center gap-2 uppercase tracking-widest text-xs">
                      <SafeIcon icon={FiCheckCircle} className="text-green-600" />
                      Konklusjon og Utfall
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <label className="text-[10px] font-black text-green-700 dark:text-green-400 uppercase tracking-widest block mb-1">Utfallstype</label>
                        <p className="text-gray-900 dark:text-white font-bold">
                          {report.outcome_type === 'confirmed_violation' ? 'Bekreftet brudd' : report.outcome_type === 'partially_confirmed' ? 'Delvis bekreftet' : report.outcome_type === 'no_violation' ? 'Ingen brudd funnet' : report.outcome_type === 'inconclusive' ? 'Uoppklart' : 'Avvist'}
                        </p>
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-green-700 dark:text-green-400 uppercase tracking-widest block mb-1">Rotårsak</label>
                        <p className="text-gray-900 dark:text-white font-bold">{report.root_cause || 'Ikke oppgitt'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-[10px] font-black text-green-700 dark:text-green-400 uppercase tracking-widest block mb-1">Oppsummering</label>
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{report.outcome_summary}</p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-[10px] font-black text-green-700 dark:text-green-400 uppercase tracking-widest block mb-1">Iverksatte tiltak</label>
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{report.remediation_action}</p>
                      </div>
                    </div>
                  </div>
                )}

                {(investigation || report.assignedInvestigatorId || assignment) && !isRUH && (
                  <ContradictionChecklist report={report} />
                )}
                <CaseAuditTimeline report={report} />
              </div>
              <div className="lg:col-span-4 space-y-6">
                <ComplianceClock report={report} />
                {(investigation || report.assignedInvestigatorId || assignment) && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-[24px] p-8 border border-green-100 dark:border-green-800 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-green-500 text-white rounded-xl flex items-center justify-center shadow-lg">
                        <SafeIcon icon={FiCheckCircle} size={20} />
                      </div>
                      <div>
                        <h3 className="font-black text-green-900 dark:text-green-100 uppercase text-xs tracking-widest">Utredning pågår</h3>
                        <p className="text-[10px] text-green-700 font-bold uppercase tracking-tight">Status: Aktiv</p>
                      </div>
                    </div>
                    <p className="text-sm text-green-800 dark:text-green-300 mb-6 font-medium leading-relaxed">
                      Saken er under formell utredning av {(report.externalHandling || assignment) ? 'ekstern part' : 'internt team'}.
                    </p>
                    <button onClick={() => navigate(`/company/investigation/form/${report.id}`)} className="w-full bg-white dark:bg-gray-800 text-green-700 dark:text-green-400 py-4 rounded-2xl font-black text-xs uppercase tracking-widest border-2 border-green-200 hover:border-green-600 transition-all shadow-sm">
                      Åpne saksmappe
                    </button>
                  </div>
                )}
                <div className="bg-indigo-600 rounded-[24px] p-8 text-white shadow-xl relative overflow-hidden group">
                  <div className="absolute -right-10 -top-10 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
                  <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                    <SafeIcon icon={FiInfo} />
                    Veileder
                  </h3>
                  <p className="text-sm text-blue-100 mb-6 leading-relaxed">
                    Er du usikker på om du følger fristene? Vår juridiske veileder gir deg full oversikt over kravene i AML.
                  </p>
                  <button onClick={() => navigate('/company/investigation-guide')} className="w-full bg-white text-indigo-600 py-3 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-indigo-50 transition-colors shadow-lg">
                    Åpne Veileder
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'evidence' && (
            <motion.div key="evidence" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <EvidenceReviewWorkspace reportId={report.id} currentUser={user} />
            </motion.div>
          )}

          {activeTab === 'actions' && isRUH && (
            <motion.div key="actions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <RUHActionPlan reportId={report.id} />
            </motion.div>
          )}

          {activeTab === 'audit' && (
            <motion.div key="audit" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <CaseAuditTimeline report={report} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {showOutcomeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white dark:bg-gray-800 rounded-[32px] w-full max-w-2xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
            <div className="p-8 border-b dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
              <div>
                <h2 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Avslutt sak</h2>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Registrer utfall og konklusjon</p>
              </div>
              <button onClick={() => setShowOutcomeModal(false)} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors">
                <SafeIcon icon={FiX} className="text-xl text-gray-400" />
              </button>
            </div>
            <div className="p-8 space-y-8 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">Utfallstype</label>
                  <select value={outcomeData.outcome_type} onChange={(e) => setOutcomeData({ ...outcomeData, outcome_type: e.target.value })} className="w-full px-5 py-4 rounded-2xl bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-indigo-500 text-gray-900 dark:text-white font-bold outline-none transition-all">
                    <option value="confirmed_violation">Bekreftet brudd</option>
                    <option value="partially_confirmed">Delvis bekreftet</option>
                    <option value="no_violation">Ingen brudd funnet</option>
                    <option value="inconclusive">Uoppklart / Inkonklusiv</option>
                    <option value="dismissed">Avvist / Henlagt</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">Rotårsak</label>
                  <select value={outcomeData.root_cause} onChange={(e) => setOutcomeData({ ...outcomeData, root_cause: e.target.value })} className="w-full px-5 py-4 rounded-2xl bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-indigo-500 text-gray-900 dark:text-white font-bold outline-none transition-all">
                    <option value="">Velg rotårsak...</option>
                    <option value="Manglende opplæring">Manglende opplæring</option>
                    <option value="Systemsvikt / Rutinesvikt">Systemsvikt / Rutinesvikt</option>
                    <option value="Personlig adferd">Personlig adferd</option>
                    <option value="Kultur / Arbeidsmiljø">Kultur / Arbeidsmiljø</option>
                    <option value="Ledelsessvikt">Ledelsessvikt</option>
                    <option value="Annet">Annet</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">Konklusjon (Oppsummering)</label>
                <textarea value={outcomeData.outcome_summary} onChange={(e) => setOutcomeData({ ...outcomeData, outcome_summary: e.target.value })} rows={4} className="w-full px-6 py-5 rounded-[24px] bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-indigo-500 text-gray-900 dark:text-white font-medium outline-none transition-all resize-none" placeholder="Beskriv hovedfunnene i granskingen..." ></textarea>
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">Korrigerende tiltak (Remediation)</label>
                <textarea value={outcomeData.remediation_action} onChange={(e) => setOutcomeData({ ...outcomeData, remediation_action: e.target.value })} rows={3} className="w-full px-6 py-5 rounded-[24px] bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-indigo-500 text-gray-900 dark:text-white font-medium outline-none transition-all resize-none" placeholder="Hvilke tiltak er gjort for å forhindre gjentakelse?" ></textarea>
              </div>
            </div>
            <div className="p-8 bg-gray-50 dark:bg-gray-800/80 border-t dark:border-gray-700 flex justify-end gap-4">
              <button onClick={() => setShowOutcomeModal(false)} className="px-8 py-4 text-gray-500 dark:text-gray-400 font-black text-xs uppercase tracking-widest hover:text-gray-900 dark:hover:text-white transition-colors">
                Avbryt
              </button>
              <button onClick={handleCloseCase} className="px-10 py-4 bg-indigo-600 text-white font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-900/20">
                Bekreft & Avslutt sak
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <AnimatePresence>
        {showAssignModal && (
          <InvestigatorSelector report={report} onCancel={() => setShowAssignModal(false)} />
        )}
        {showEvidenceModal && (
          <EvidenceRequestModal report={report} onCancel={() => setShowEvidenceModal(false)} onSuccess={() => { setShowEvidenceModal(false); alert("Bevisforespørsel er sendt."); }} />
        )}
        {showHandoffModal && (
          <InvestigatorHandoffModal isOpen={showHandoffModal} onClose={() => setShowHandoffModal(false)} report={report} onComplete={() => { setShowHandoffModal(false); fetchAssignment(); }} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReportDetails;